import { useState } from "react";
import { Search, Loader2, ShieldCheck, ArrowLeft, Clock, Cog, CheckCircle2, XCircle, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, whatsappLink, COMPANY_NAME, formatApiErrorDetail } from "@/lib/api";

const STATUS_STEPS = [
  { key: "pendente", label: "Pedido recebido", icon: Clock },
  { key: "em_processamento", label: "Em processamento", icon: Cog },
  { key: "concluido", label: "Concluído", icon: CheckCircle2 },
];

export default function TrackOrder() {
  const [protocol, setProtocol] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    if (!protocol.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/orders/track/${protocol.trim().toUpperCase()}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Protocolo não encontrado");
    } finally {
      setLoading(false);
    }
  };

  const cancelled = result?.status === "cancelado";
  const currentIdx = result ? STATUS_STEPS.findIndex((s) => s.key === result.status) : -1;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F294A] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/10"><ShieldCheck className="h-5 w-5 text-emerald-400" /></span>
            <span className="font-heading font-bold">Central <span className="text-emerald-400">MEI</span></span>
          </a>
          <a href="/" className="text-sm text-slate-300 hover:text-white flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Início</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">Acompanhar pedido</h1>
          <p className="text-slate-500 mt-2">Digite o número do protocolo que você recebeu ao solicitar o serviço.</p>
        </div>

        <form onSubmit={search} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="track-input" value={protocol} onChange={(e) => setProtocol(e.target.value)} placeholder="Ex: CM26100712AB3" className="pl-9 h-12 uppercase" />
          </div>
          <Button data-testid="track-submit" type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12 px-7 font-bold gap-2">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />} Consultar
          </Button>
        </form>

        {error && <p data-testid="track-error" className="text-center text-red-600 bg-red-50 rounded-lg px-4 py-3 mt-6 max-w-xl mx-auto">{error}</p>}

        {result && (
          <div data-testid="track-result" className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-2 pb-5 border-b border-slate-100">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Protocolo</p>
                <p className="font-heading font-extrabold text-xl text-[#0F294A]">{result.protocol}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Cliente</p>
                <p className="font-semibold text-slate-700">{result.name}</p>
              </div>
            </div>

            <div className="py-5">
              <p className="text-sm text-slate-500">Serviço</p>
              <p className="font-semibold text-slate-900">{result.service_name}</p>
            </div>

            {cancelled ? (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 flex items-center gap-3">
                <XCircle className="h-6 w-6 text-slate-400" />
                <p className="font-medium text-slate-600">Este pedido foi cancelado.</p>
              </div>
            ) : (
              <div className="relative flex justify-between">
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200" />
                <div className="absolute top-5 left-0 h-0.5 bg-emerald-500 transition-all" style={{ width: `${(currentIdx / (STATUS_STEPS.length - 1)) * 100}%` }} />
                {STATUS_STEPS.map((s, i) => {
                  const done = i <= currentIdx;
                  return (
                    <div key={s.key} className="relative z-10 flex flex-col items-center text-center flex-1">
                      <span className={`grid place-items-center h-10 w-10 rounded-full border-2 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 text-slate-300"}`}>
                        <s.icon className="h-5 w-5" />
                      </span>
                      <p className={`mt-2 text-xs font-medium ${done ? "text-emerald-700" : "text-slate-400"}`}>{s.label}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-slate-100">
              <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${result.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {result.payment_status === "paid" ? "Pagamento confirmado" : "Pagamento pendente"}
              </span>
              <a href={whatsappLink(`Olá! Gostaria de acompanhar meu pedido ${result.protocol} na ${COMPANY_NAME}.`)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-2"><MessageCircle className="h-4 w-4" /> Falar no WhatsApp</Button>
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
